import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  EyeSlashed,
  Mail,
  Pass,
  User,
} from "../../images/icons/JsxIcons";
import { NotificationService } from "../../services/Notification.service";
import { AuthV2Service } from "../../services/authV2Service";
import { encryptData } from "../../services/encryption.service";
import { checkIfPassIsGood } from "../../services/utils";
import { useStoreAuthV2 } from "../../store/AuthV2Store";
import { useStorePageManager } from "../../store/PageManage";
import { useStoreUserManager } from "../../store/UserManage";
import InputPhoneNumber from "../cells/InputPhoneNumber";

export const AuthV2CompletePanel = ({
  currentState = 1,
  panelState,
  setPanelState,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    identifier,
    identifierType,
    formData,
    setFormData,
    resetAuthV2State,
  } = useStoreAuthV2();

  const [loading, setLoading] = useState(false);
  const [seePassword, setSeePassword] = useState(false);

  const [lastname, setLastname] = useState(formData.lastname || "");
  const [firstname, setFirstname] = useState(formData.firstname || "");
  const [birthdate, setBirthdate] = useState(formData.birthdate || "");
  const [secondaryContact, setSecondaryContact] = useState(
    formData.secondaryContact || "",
  );
  const [password, setPassword] = useState(formData.password || "");

  const [phoneData, setPhoneData] = useState({
    telephone: "",
    indicatif: "",
  });
  const [stateTelephone, setStateTelephone] = useState({
    focus: false,
    error: false,
    good: false,
  });
  const [curentCountrySelected, setCurentCountrySelected] = useState();

  const [stateLastname, setStateLastname] = useState({
    focus: false,
    error: false,
    good: false,
  });
  const [stateFirstname, setStateFirstname] = useState({
    focus: false,
    error: false,
    good: false,
  });
  const [stateBirthdate, setStateBirthdate] = useState({
    focus: false,
    error: false,
    good: false,
  });
  const [stateEmail, setStateEmail] = useState({
    focus: false,
    error: false,
    good: false,
  });
  const [stateMdp, setStateMdp] = useState({
    focus: false,
    error: false,
    good: false,
  });

  const needsPassword = identifierType === "EMAIL";
  const needsPhoneSecondary = identifierType === "EMAIL";
  const needsEmailSecondary = identifierType === "PHONE";
  const hasVerifiedPhoneSecondary =
    needsPhoneSecondary && Boolean(formData.secondaryContact);

  useEffect(() => {
    if (lastname.length > 0) {
      setStateLastname({
        ...stateLastname,
        error: lastname.length < 2,
        good: lastname.length >= 2,
      });
    } else {
      setStateLastname({ ...stateLastname, error: false, good: false });
    }
  }, [lastname]);

  useEffect(() => {
    if (firstname.length > 0) {
      setStateFirstname({
        ...stateFirstname,
        error: firstname.length < 2,
        good: firstname.length >= 2,
      });
    } else {
      setStateFirstname({ ...stateFirstname, error: false, good: false });
    }
  }, [firstname]);

  useEffect(() => {
    if (birthdate) {
      const ageCheck = AuthV2Service.validateAge(birthdate);
      setStateBirthdate({
        ...stateBirthdate,
        error: !ageCheck.isValid,
        good: ageCheck.isValid,
      });
    } else {
      setStateBirthdate({ ...stateBirthdate, error: false, good: false });
    }
  }, [birthdate]);

  useEffect(() => {
    if (needsEmailSecondary && secondaryContact.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(secondaryContact)) {
        setStateEmail({ ...stateEmail, error: false, good: true });
      } else {
        setStateEmail({ ...stateEmail, error: true, good: false });
      }
    } else {
      setStateEmail({ ...stateEmail, error: false, good: false });
    }
  }, [secondaryContact]);

  useEffect(() => {
    if (phoneData.telephone.length > 2) {
      if (phoneData.telephone.length >= 8) {
        setStateTelephone({ ...stateTelephone, error: false, good: true });
      } else {
        setStateTelephone({ ...stateTelephone, error: true, good: false });
      }
    } else {
      setStateTelephone({ ...stateTelephone, error: false, good: false });
    }
  }, [phoneData.telephone]);

  useEffect(() => {
    if (password.length > 0) {
      if (password.length >= 8 && checkIfPassIsGood(password)) {
        setStateMdp({ ...stateMdp, error: false, good: true });
      } else {
        setStateMdp({ ...stateMdp, error: true, good: false });
      }
    } else {
      setStateMdp({ ...stateMdp, error: false, good: false });
    }
  }, [password]);

  const handleUptadeTelephone = (e) => {
    let newTelephone = e.target.value.replace(/[^0-9]/g, "");
    setPhoneData({ ...phoneData, telephone: newTelephone });
    e.target.value = newTelephone;
  };

  const isFormValid = () => {
    const baseValid =
      stateLastname.good && stateFirstname.good && stateBirthdate.good;
    // Pour PHONE : mdp optionnel → valide si vide OU si bien rempli
    // Pour EMAIL : mdp obligatoire → doit être good
    const passwordValid = needsPassword
      ? stateMdp.good
      : password.length === 0 || stateMdp.good;

    if (needsEmailSecondary) return baseValid && stateEmail.good && passwordValid;
    if (needsPhoneSecondary)
      return (
        baseValid &&
        (hasVerifiedPhoneSecondary ||
          (stateTelephone.good && curentCountrySelected)) &&
        passwordValid
      );

    return baseValid && passwordValid;
  };

  const getSecondaryContact = () => {
    if (needsEmailSecondary) return secondaryContact;
    if (needsPhoneSecondary) {
      if (hasVerifiedPhoneSecondary) return formData.secondaryContact;
      let tel = phoneData.indicatif + phoneData.telephone;
      if (tel[0] !== "+") tel = "+" + tel;
      return tel;
    }
    return "";
  };

  const handleComplete = async () => {
    if (!isFormValid() || loading) return;

    setLoading(true);
    try {
      const data = {
        identifier,
        firstname,
        lastname,
        birthdate,
        secondaryContact: getSecondaryContact(),
      };

      if (password) {
        data.password = password;
      }

      const response = await AuthV2Service.completeRegistration(data);
      const userData = response.data;

      const dataToken = {
        access_token: userData.access_token,
        refresh_token: userData.refresh_token,
      };
      const encryptToken = await encryptData(dataToken);
      localStorage.setItem("token", encryptToken);

      const dataUserSave = {
        token: userData.token || "",
        email: userData.email,
      };
      const dataEncrypted = await encryptData(dataUserSave);
      localStorage.setItem("user", dataEncrypted);

      useStoreUserManager.getState().handleUpdateUser({
        id: userData.id,
        lastname: userData.lastname,
        firstname: userData.firstname,
        location: userData.location || "",
        connected: true,
        profil: userData.profile + `${userData.enableHost ? "/hote" : ""}`,
        mail: userData.email,
        contact: userData.contact || null,
        emailVerified: userData.mailActivatedAt && true,
        contactChecked: userData.contactActivatedAt && true,
        primaryIdentifierType: identifierType || "EMAIL",
        identityDoc: userData.identityDoc ?? null,
        pieceChecked: userData.identityDoc ? 1 : 0,
        profilImgUrl: userData.avatar ?? null,
        isSocial: false,
        typeSocial: "",
        enableHost: userData.enableHost || false,
        payment_method_id: userData.payment_method_id ?? 0,
      });

      NotificationService.addToQueue(
        0,
        t("modals.connexionPanel.authV2.registrationSuccess"),
      );

      resetAuthV2State();
      useStorePageManager.getState().handleUpdateBlockScreen(false);
      useStorePageManager.getState().handleUpdateConnexionPanelState(false);

      const whereDirectGo = useStorePageManager.getState().nextLoadingNave;
      if (whereDirectGo) {
        useStorePageManager.getState().handleUpdateNextLoadingNave("");
        navigate(whereDirectGo);
      }
    } catch (error) {
      const errorMsg = error.message || "";

      if (errorMsg.includes("majeur") || errorMsg.includes("underage")) {
        NotificationService.addToQueue(
          2,
          t("modals.connexionPanel.authV2.underage"),
        );
      } else {
        NotificationService.addToQueue(
          2,
          errorMsg || t("modals.connexionPanel.authV2.genericError"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setFormData({
      firstname,
      lastname,
      birthdate,
      secondaryContact: getSecondaryContact(),
      password,
    });
    setPanelState({
      ...panelState,
      authV2Complete: 2,
      authV2Otp: 1,
    });
  };

  const handleContactSupport = () => {
    window.open(
      "https://api.whatsapp.com/send?phone=+2250556094636&text=Bonjour%2C%20j%27ai%20besoin%20d%27aide%20pour%20mon%20inscription",
      "_blank",
    );
  };

  return (
    <div
      className={`contains inscription-part ${
        currentState === 0
          ? "go-left-side"
          : currentState === 1
            ? "come-center"
            : "go-right-side"
      } | flcolm`}
    >
      <button className="back-btn | btn" onClick={handleBack}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="var(--gray-900)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <span className="auth-v2-panel-title">
        {t("modals.connexionPanel.authV2.completeTitle")}
      </span>
      <label className="auth-v2-welcome">
        {t("modals.connexionPanel.authV2.completeWelcome")}
      </label>
      <p className="auth-v2-subtitle">
        {needsEmailSecondary
          ? t("modals.connexionPanel.authV2.completeHintEmail")
          : t("modals.connexionPanel.authV2.completeHintPhone")}
      </p>

      <div className="input-field | flcolm">
        <div className="input | rlve">
          <User color={stateLastname.focus ? "var(--gray-600)" : "none"} />
          <input
            onBlur={() => setStateLastname({ ...stateLastname, focus: false })}
            onFocus={() => setStateLastname({ ...stateLastname, focus: true })}
            onChange={(e) => setLastname(e.target.value)}
            value={lastname}
            placeholder={t("modals.connexionPanel.authV2.placeholderLastname")}
            type="text"
            autoComplete="family-name"
          />
          {!stateLastname.focus && stateLastname.error && (
            <div className="detect-issue-input">
              <div className="chevron"></div>
              <span>{t("modals.connexionPanel.authV2.issueNameMin")}</span>
            </div>
          )}
        </div>

        <div className="input | rlve">
          <User color={stateFirstname.focus ? "var(--gray-600)" : "none"} />
          <input
            onBlur={() =>
              setStateFirstname({ ...stateFirstname, focus: false })
            }
            onFocus={() =>
              setStateFirstname({ ...stateFirstname, focus: true })
            }
            onChange={(e) => setFirstname(e.target.value)}
            value={firstname}
            placeholder={t("modals.connexionPanel.authV2.placeholderFirstname")}
            type="text"
            autoComplete="given-name"
          />
          {!stateFirstname.focus && stateFirstname.error && (
            <div className="detect-issue-input">
              <div className="chevron"></div>
              <span>{t("modals.connexionPanel.authV2.issueNameMin")}</span>
            </div>
          )}
        </div>

        <div className="input-date-wrapper | flcolm">
          <label htmlFor="authV2-birthdate" className="input-date-label">
            {t("modals.connexionPanel.authV2.placeholderBirthdate")}
          </label>
          <div className={`input input-date | rlve ${birthdate ? "input-date-has-value" : ""}`}>
            <Calendar
              color={stateBirthdate.focus ? "var(--gray-600)" : "none"}
              stroke={stateBirthdate.focus ? "var(--gray-600)" : "#9CA3AF"}
            />
            <input
              id="authV2-birthdate"
              onBlur={() =>
                setStateBirthdate({ ...stateBirthdate, focus: false })
              }
              onFocus={() =>
                setStateBirthdate({ ...stateBirthdate, focus: true })
              }
              onChange={(e) => setBirthdate(e.target.value)}
              value={birthdate}
              type="date"
              max={
                new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                  .toISOString()
                  .split("T")[0]
              }
              autoComplete="bday"
            />
            {!stateBirthdate.focus && stateBirthdate.error && (
              <div className="detect-issue-input">
                <div className="chevron"></div>
                <span>{t("modals.connexionPanel.authV2.issueAge")}</span>
              </div>
            )}
          </div>
        </div>

        {needsEmailSecondary && (
          <div className="input | rlve">
            <Mail color={stateEmail.focus ? "var(--gray-600)" : "none"} />
            <input
              onBlur={() => setStateEmail({ ...stateEmail, focus: false })}
              onFocus={() => setStateEmail({ ...stateEmail, focus: true })}
              onChange={(e) => setSecondaryContact(e.target.value)}
              value={secondaryContact}
              placeholder={t("modals.connexionPanel.authV2.placeholderEmail")}
              type="email"
              autoComplete="email"
            />
            {!stateEmail.focus && stateEmail.error && (
              <div className="detect-issue-input">
                <div className="chevron"></div>
                <span>{t("modals.connexionPanel.authV2.issueEmail")}</span>
              </div>
            )}
          </div>
        )}

        {needsPhoneSecondary && !hasVerifiedPhoneSecondary && (
          <InputPhoneNumber
            dataInscription={phoneData}
            setDataInscription={setPhoneData}
            setStateTelephone={setStateTelephone}
            stateTelephone={stateTelephone}
            handleUptadeTelephone={handleUptadeTelephone}
            curentCountrySelected={curentCountrySelected}
            setCurentCountrySelected={setCurentCountrySelected}
            setindicatif={(val) =>
              setPhoneData((prev) => ({ ...prev, indicatif: val }))
            }
            indicatif={phoneData.indicatif}
          />
        )}

        <div className="input password | rlve">
          <Pass color={stateMdp.focus ? "var(--gray-600)" : "none"} />
          <input
            onBlur={() => setStateMdp({ ...stateMdp, focus: false })}
            onFocus={() => setStateMdp({ ...stateMdp, focus: true })}
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            placeholder={t(
              needsPassword
                ? "modals.connexionPanel.authV2.placeholderPassword"
                : "modals.connexionPanel.authV2.placeholderPasswordOptional",
            )}
            type={seePassword ? "text" : "password"}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setSeePassword(!seePassword)}
            className="see-pass | btn"
          >
            <EyeSlashed
              stroke={seePassword ? "var(--gray-300)" : "#9CA3AF"}
            />
          </button>
          {!stateMdp.focus && stateMdp.error && (
            <div className="detect-issue-input">
              <div className="chevron"></div>
              <span>{t("modals.connexionPanel.authV2.issuePassword")}</span>
            </div>
          )}
        </div>
      </div>

      <div className="auth-v2-footer | flrow allcenter">
        <button
          onClick={handleContactSupport}
          className="btn-outline | btn flex"
        >
          {t("modals.connexionPanel.authV2.contactSupport")}
        </button>
        <button
          onClick={handleComplete}
          className="next-btn | btn flex"
          disabled={!isFormValid() || loading}
        >
          {loading
            ? t("modals.connexionPanel.authV2.loading")
            : t("modals.connexionPanel.authV2.finalizeBtn")}
        </button>
      </div>
    </div>
  );
};
